import { CallExpressionShape, type Shape, Record as SdkRecord } from '@servicenow/sdk-build-core'

/**
 * Given a shape that is (or resolves to) a factory call like `PlaybookTriggerType({...})`,
 * returns the first argument as an ObjectShape — i.e. the config passed to the factory.
 * Resolves identifiers and property access chains before checking for call expressions.
 *
 * When the shape has already been processed into a Record by another plugin,
 * falls back to the Record's original source to recover the pre-processed CallExpressionShape.
 */
export function getFactoryCallConfig(shape: Shape): ReturnType<Shape['ifObject']> {
    const resolved = shape.isResolvable() ? shape.asResolvable().resolve() : shape

    const directCE = resolved.if(CallExpressionShape)
    if (directCE) {
        return directCE.getArgument(0)?.ifObject()
    }

    if (resolved instanceof SdkRecord) {
        const source = resolved.getSource()
        if (source instanceof CallExpressionShape) {
            return source.getArgument(0)?.ifObject()
        }
    }

    return undefined
}

/**
 * Extract the sysId from a variable entry shape.
 * Entries are Column calls like `StringColumn({ sysId: '...', ... })` (CallExpressionShape)
 * or plain objects with a `sysId` property (ObjectShape).
 */
export function extractVariableSysId(shape: Shape): string | undefined {
    const obj = shape.if(CallExpressionShape)?.getArgument(0)?.ifObject() ?? shape.ifObject()
    return obj?.get('sysId', false)?.ifString()?.getValue()
}
